import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useWindowSize } from "lib/hooks";
import ReactGA from "react-ga";
import { sendPageView } from "lib/googleAnalytics/page";
import log from "loglevel";
import { Container, Row, Col, Card } from "react-bootstrap";
import { Breadcrumb, BreadcrumbData, Page } from "@avlino-inc/alenza-react";
import MainNav from "components/nav/MainNav";
import TeamUsersTable from "components/dashboard/team/TeamUsersTable";
import {
  getAccountReducerResponse,
  getTeamReducerResponse,
  getTeamUsersReducerResponse,
  getJwtReducerResponse,
} from "store/selectors";
import {
  getAccountAction,
  getTeamAction,
  getTeamUsersAction,
  decodeJwtAction,
} from "store/actions/actionCreators";
import {
  Account,
  Team,
  TeamUser,
  JWT,
  GetTeamRequestType,
  GetTeamUsersRequestType,
  GetAccountRequestType,
} from "types";
import * as routes from "routes";
import styles from "./AccountTeamPage.module.scss";

const AccountTeamPage = ({
  accountName,
  teamName,
  authed,
}: {
  accountName: string;
  teamName: string;
  authed: Promise<boolean>;
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const windowSize = useWindowSize();

  useEffect(() => {
    if (authed) {
      authed.then((allow) => {
        log.info("Allow: " + allow);
        if (allow) {
          log.info("Authenticated. Stay on page...");
        } else {
          log.info("Redirect back...");
          history.push(routes.LOGIN);
        }
      });
    } else {
      history.push(routes.LOGIN);
    }
  }, [authed, history]);

  useEffect(() => {
    log.info("AccountTeamPage useEffect()...");
    dispatch(decodeJwtAction({}));
    const accountRequest: GetAccountRequestType = {
      params: {
        name: accountName,
      },
    };
    const teamsRequest: GetTeamRequestType = {
      params: {
        account_name: accountName,
        team_name: teamName,
      },
    };
    const teamUsersRequest: GetTeamUsersRequestType = {
      params: {
        account_name: accountName,
        team_name: teamName,
      },
    };
    dispatch(getAccountAction(accountRequest));
    dispatch(getTeamAction(teamsRequest));
    dispatch(getTeamUsersAction(teamUsersRequest));
  }, [dispatch, accountName, teamName]);

  /**
   * Google Analytics
   */
  useEffect(() => {
    sendPageView(location.pathname + location.search);
  }, [location]);
  
  const jwtPayloadData: JWT = useSelector((state) =>
    getJwtReducerResponse(state, "data")
  );
  const accountData: Account = useSelector((state) =>
    getAccountReducerResponse(state, "data")
  );
  const teamData: Team = useSelector((state) =>
    getTeamReducerResponse(state, "data")
  );
  const teamsUsersData: TeamUser[] = useSelector((state) =>
    getTeamUsersReducerResponse(state, "data")
  );

  const breadcrumbItems = useMemo<BreadcrumbData[]>(
    () => [
      {
        url: routes.DASHBOARD_MAIN,
        label: "Command Center",
      },
      {
        url: routes.ACCOUNTS,
        label: "Accounts",
      },
      {
        url: routes.ACCOUNT(accountName),
        label: `${accountName}`,
      },
      {
        url: routes.ACCOUNT(accountName),
        label: `Team`,
      },
      {
        url: routes.ACCOUNT_TEAM(accountName, teamName),
        label: `${teamName}`,
      },
    ],
    []
  );

  return (
    <div id="Account">
      <MainNav
        first_name={jwtPayloadData && jwtPayloadData.first_name}
        last_name={jwtPayloadData && jwtPayloadData.last_name}
        username={jwtPayloadData && jwtPayloadData.username}
        email={jwtPayloadData && jwtPayloadData.email}
      />
      <Container
        className="text-center"
        fluid={true}
        style={{ minWidth: "100%" }}
      >
        <Page.Header>
          <Breadcrumb items={breadcrumbItems} onClickItem={history.push} />
        </Page.Header>
        <Row className="justify-content-md-center">
          <Col xs={12} md={10} lg={8} xl={6}>
            <Card>
              <Container>
                <Row className={styles["account-team-page-card__team-info"]}>
                  <Col xs={4} sm={4} md={4} />
                  <Col xs={4} sm={4} md={4}>
                    <p
                      className={
                        styles["account-team-page-card__team-info__team-title"]
                      }
                    >
                      {teamData && teamData.name} Team
                    </p>
                    <p
                      className={
                        styles[
                          "account-team-page-card__team-info__team-description"
                        ]
                      }
                    >
                      {teamData && teamData.description}
                    </p>
                  </Col>
                  <Col xs={4} sm={4} md={4} />
                </Row>
                <Row>
                  <Col>
                    <Card.Body>
                      <TeamUsersTable
                        teamUsers={teamsUsersData}
                        showEmail={true}
                        showRole={true}
                        showPic={true}
                        cardTable={true}
                        showSearch={true}
                      />
                    </Card.Body>
                  </Col>
                </Row>
              </Container>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AccountTeamPage;
